/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import {useTranslation} from 'react-i18next'
import OfferBasicForm from 'components/Offers/forms/OfferBasicForm'
import OfferEventsForm from 'components/Offers/forms/OfferEventsForm'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiStepsNav from 'components/Forms/UiStepsNav'
import clsx from 'clsx'
import OfferUpsells from './OfferUpsells'
import {Offer} from 'types/entities/Offer'
import {Company} from 'types/entities/Company'
import {OfferBasicInputs} from 'components/Offers/schemas/offerBasicSchema'
import UiButton, {UiButtonSize, UiButtonStyle} from 'components/Ui/UiButton'
import {ArrowUturnLeftIcon} from '@heroicons/react/24/solid'
import UiFormStickyFooter from 'components/Forms/UiFormStickyFooter'
import {CreateOfferStep} from 'components/Offers/enums/CreateOfferStep'
import {useCreateOfferOrchestration} from 'utils/hooks/useCreateOfferOrchestration'
import Dates from 'utils/Dates'
import { capturePosthogEvent } from 'utils/posthogUtils'

interface CreateOfferModalProps {
    onCreated?: (offer?: Offer) => void
    companyId?: number | null
    company?: Company | null
    leadId?: number | null
    preferredTime?: string
    embedded?: boolean
    disableCompanyChange?: boolean
    defaultValues?: Partial<OfferBasicInputs>
    onGuardStateChange?: (offerId: number | null) => void
}

const CreateOfferModal: React.FC<CreateOfferModalProps> = ({
    onCreated,
    companyId,
    company,
    leadId,
    preferredTime,
    embedded = false,
    disableCompanyChange = false,
    defaultValues,
    onGuardStateChange,
}) => {
    const { t } = useTranslation()

    const onCreatedWithTracking = React.useCallback(
        (offer?: Offer) => {
            capturePosthogEvent('offer_created', {
                offer_id: offer?.id,
                has_company: !!offer?.company_id,
                has_lead: !!offer?.lead_id,
            })
            onCreated?.(offer)
        },
        [onCreated]
    )

    const {
        activeStep,
        setActiveStep,
        maxReachedStep,
        isCreatingOffer,
        offerCreationError,
        showResumePrompt,
		isNextDisabled,
        isDiscardingDraft,
        isLeadDefaultsLoading,
        isUpsellsLoading,
        effectiveCompanyId,
        mergedBasicFormDefaultValues,
        selectedCompanyForForm,
        steps,
        selectedOffer,
        draftOfferId,
        bookingProcess,
        selectedUpsells,
        handleCreateOfferFromFirstStep,
        handlePackageItemsSuccess,
        handleSuccess,
        submitPackageUpsells,
        handleResumeDraft,
        handleDiscardDraft,
    } = useCreateOfferOrchestration({
        onCreated: onCreatedWithTracking,
        companyId,
        company,
        leadId,
        preferredTime,
        embedded,
        disableCompanyChange,
        defaultValues,
        onGuardStateChange,
    })


    return (
        <UiFormModalShell
            className={clsx('CreateOfferModal')}
            title={t('Create Offer')}
            subtitle={t('Fill in the offer details below')}
            showHeader={false}
            showHeaderCompact={!embedded}
            headerBottom={
                <UiStepsNav
                    activeStep={activeStep}
                    maxReachedStep={maxReachedStep}
                    onChange={setActiveStep}
                    steps={steps}
                />
            }
            footer={
                activeStep === CreateOfferStep.Upsells && (
                    <UiFormStickyFooter>
                        <div className="flex w-full justify-between gap-4">
                            <UiButton
                                className={clsx('BookingFooterStepsBack')}
                                icon={<ArrowUturnLeftIcon className={clsx('w-5 h-5')} />}
                                size={UiButtonSize.Medium}
                                style={UiButtonStyle.Gray}
                                title={t('Back')}
                                onClick={() => setActiveStep(CreateOfferStep.PackageItems)}
                            />
                            <UiButton
                                disabled={isUpsellsLoading || !bookingProcess.selectedTimeslot}
                                id="ButtonSubmitUpsells"
                                size={UiButtonSize.Medium}
                                style={isNextDisabled ? UiButtonStyle.Gray : UiButtonStyle.Primary}
                                title={selectedUpsells.length === 0 ? t('Continue without upsells') : t('Continue')}
                                onClick={submitPackageUpsells}
                            />
                        </div>
                    </UiFormStickyFooter>
                )
            }
        >
            {showResumePrompt && draftOfferId ? (
                <div className="p-6 flex flex-col gap-5">
                    <div className="flex items-start gap-3">
                        <div>
                            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                                {t('Resume Draft Offer?')}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                {t(
                                    'You have an unfinished offer (#{{id}}) from your last session. Would you like to continue where you left off, or start fresh?',
                                    { id: draftOfferId }
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 pt-2 border-t dark:border-gray-700">
                        <UiButton
                            style={UiButtonStyle.Primary}
                            size={UiButtonSize.Medium}
                            onClick={handleResumeDraft}
                            className="w-full justify-center"
                        >
                            {t('Continue Draft')}
                        </UiButton>
                        <UiButton
                            style={UiButtonStyle.DELETE}
                            size={UiButtonSize.Medium}
                            loading={isDiscardingDraft}
                            disabled={isDiscardingDraft}
                            onClick={handleDiscardDraft}
                            className="w-full justify-center"
                        >
                            {t('Delete & Start Fresh')}
                        </UiButton>
                    </div>
                </div>
            ) : (
                <div className={clsx('CreateOfferModal-content w-full flex flex-col space-y-6', embedded && 'pt-4')}>
                    <div className={'flex flex-row justify-between border-b border-gray-200 dark:border-gray-800'}>
                        {embedded && (
                            <div className="px-4 pb-2">
                                <UiStepsNav
                                    activeStep={activeStep}
                                    maxReachedStep={maxReachedStep}
                                    onChange={setActiveStep}
                                    steps={steps}
                                />
                            </div>
                        )}
                        <div className={'grow flex place-content-center place-items-center'}>
                            <span>{bookingProcess.filter && Dates.date(bookingProcess.filter.date)}</span>
                        </div>

                        {offerCreationError && (
                            <div className="p-4 mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <p className="text-sm text-red-700 dark:text-red-400">{offerCreationError}</p>
                            </div>
                        )}
                    </div>
                    {activeStep === CreateOfferStep.Details && (
                        <>
                            {isLeadDefaultsLoading ? (
                                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                    <p className="text-sm text-yellow-700 dark:text-yellow-400">
                                        {t('Loading lead details...')}
                                    </p>
                                </div>
                            ) : (
                                <OfferBasicForm
                                    onChange={(values) => {
                                        if (!isCreatingOffer) {
                                            handleCreateOfferFromFirstStep(values)
                                        }
                                    }}
                                    preferredTime={preferredTime}
                                    companyIdFromContext={effectiveCompanyId}
                                    selectedCompanyDefault={selectedCompanyForForm}
                                    disableCompanyChange={disableCompanyChange}
                                    defaultValues={mergedBasicFormDefaultValues}
                                />
                            )}
                        </>
                    )}

                    {activeStep === CreateOfferStep.PackageItems && (
                        <OfferEventsForm
                            onBack={() => setActiveStep(CreateOfferStep.Details)}
                            onSuccess={handlePackageItemsSuccess}
                        />
                    )}

                    {activeStep === CreateOfferStep.Upsells && selectedOffer?.id && (
                        <OfferUpsells
                            onBack={() => setActiveStep(CreateOfferStep.PackageItems)}
                            onSuccess={handleSuccess}
                            hideFooter={true}
                        />
                    )}

                    {activeStep === CreateOfferStep.Upsells && !selectedOffer?.id && (
                        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                            <p className="text-sm text-yellow-700 dark:text-yellow-400">
                                {t('Loading offer details...')}
                            </p>
                        </div>
                    )}
                </div>
            )}
        </UiFormModalShell>
    )
}

export default CreateOfferModal
